"use client";

import {
  type ChangeEvent,
  type KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/* -------------------------------------------------------------------------- */
/* EditableValue                                                                */
/* -------------------------------------------------------------------------- */

export interface EditableValueProps {
  /** Current display value (always a string — formatting done by the caller). */
  value: string;
  /** Called with the trimmed new string when the user commits the edit. */
  onSave: (next: string) => void;
  /** Controls `inputMode` default. */
  type?: "text" | "number";
  /** Text alignment of both display and input. */
  align?: "left" | "right";
  /** Extra class names forwarded to both display and input elements. */
  className?: string;
  /** Accessible label for the element. */
  ariaLabel?: string;
  /** Override for the input's inputMode attribute. */
  inputMode?: "text" | "numeric" | "decimal";
  /** Optional string rendered before the value (e.g. "$"). */
  prefix?: string;
  /** Optional string rendered after the value (e.g. "%"). */
  suffix?: string;
  /** When true, enter edit mode and focus/select the input on mount/update. */
  autoFocus?: boolean;
  /** Called after an auto-focus request is consumed. */
  onAutoFocus?: () => void;
  /**
   * When true, the draft is re-formatted with thousands separators on every
   * keystroke and commas are stripped before `onSave` fires. Set on numeric
   * fields so the user sees `1,250,000` while typing instead of `1250000`.
   */
  formatThousands?: boolean;
}

/** Re-apply thousands separators to a numeric draft as the user types. */
function formatThousandsStr(raw: string): string {
  const cleaned = raw.replace(/,/g, "");
  const match = cleaned.match(/^(-?)(\d*)(\.\d*)?$/);
  if (!match) return raw;
  const [, sign, integerPart, decimalPart] = match;
  if (!integerPart) return cleaned;
  const withCommas = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${sign ?? ""}${withCommas}${decimalPart ?? ""}`;
}

/**
 * Inline click-to-edit field.  Renders as a `<button>` with a pencil-icon
 * affordance at rest, and switches to an `<input>` on click.
 *
 * Commits on Enter or blur; cancels on Escape.
 */
export function EditableValue({
  value,
  onSave,
  type = "text",
  align = "left",
  className,
  ariaLabel,
  inputMode,
  prefix,
  suffix,
  autoFocus = false,
  onAutoFocus,
  formatThousands = false,
}: EditableValueProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cancelledRef = useRef(false);
  const committedRef = useRef(false);
  const autoFocusConsumedRef = useRef(false);
  const pendingFocusRef = useRef(false);
  const pendingAutoFocusNotificationRef = useRef(false);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useLayoutEffect(() => {
    if (!editing || !pendingFocusRef.current) return;
    const input = inputRef.current;
    if (!input) return;

    pendingFocusRef.current = false;
    input.focus();
    input.select();

    if (pendingAutoFocusNotificationRef.current) {
      pendingAutoFocusNotificationRef.current = false;
      onAutoFocus?.();
    }
  }, [editing, onAutoFocus]);

  useEffect(() => {
    if (!autoFocus) {
      autoFocusConsumedRef.current = false;
      return;
    }
    if (autoFocusConsumedRef.current) return;
    autoFocusConsumedRef.current = true;
    startEdit({ notifyAutoFocus: true });
  }, [autoFocus, onAutoFocus]);

  function startEdit(options?: { notifyAutoFocus?: boolean }) {
    cancelledRef.current = false;
    committedRef.current = false;
    setDraft(formatThousands ? formatThousandsStr(value) : value);
    pendingFocusRef.current = true;
    pendingAutoFocusNotificationRef.current = options?.notifyAutoFocus ?? false;
    setEditing(true);
  }

  function commit() {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    if (committedRef.current) return;
    committedRef.current = true;
    const next = formatThousands
      ? draft.replace(/,/g, "").trim()
      : draft.trim();
    const baseline = formatThousands
      ? value.replace(/,/g, "").trim()
      : value.trim();
    if (next !== baseline) onSave(next);
    setEditing(false);
  }

  function cancel() {
    cancelledRef.current = true;
    setDraft(value);
    setEditing(false);
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  }

  if (editing) {
    const inputEl = (
      <input
        ref={inputRef}
        type="text"
        inputMode={inputMode ?? (type === "number" ? "decimal" : "text")}
        value={draft}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const next = formatThousands
            ? formatThousandsStr(e.target.value)
            : e.target.value;
          setDraft(next);
        }}
        onBlur={commit}
        onKeyDown={onKey}
        className={[
          // reset
          "appearance-none bg-(--bg-white) font-[inherit] text-inherit",
          // sizing
          "min-w-[60px] max-w-full w-auto",
          // spacing
          "py-[1px] px-[4px] -mx-[5px] -my-[2px]",
          // border
          "border border-(--fg-brand) rounded-[4px]",
          // focus ring
          "outline-none focus:shadow-[0_0_0_2px_color-mix(in_oklab,var(--fg-brand)_20%,transparent)]",
          // numeric
          "[font-variant-numeric:inherit]",
          // alignment
          align === "right" ? "text-right" : "text-left",
          className ?? "",
        ].join(" ")}
        aria-label={ariaLabel}
      />
    );

    if (!prefix && !suffix) return inputEl;

    return (
      <span
        className={[
          "inline-flex items-center gap-[3px]",
          align === "right" ? "justify-end" : "justify-start",
        ].join(" ")}
      >
        {prefix && <span className="text-(--fg-muted) shrink-0">{prefix}</span>}
        {inputEl}
        {suffix && <span className="text-(--fg-muted) shrink-0">{suffix}</span>}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => startEdit()}
      aria-label={ariaLabel ?? `Edit ${value}`}
      className={[
        // reset
        "appearance-none bg-transparent p-0 m-0 font-[inherit] text-inherit",
        // layout
        "inline-flex items-center gap-[4px] cursor-text",
        // spacing (negative margin trick matches original)
        "rounded-[4px] py-[1px] px-[5px] -mx-[5px] -my-[1px]",
        // dashed bottom border
        "border-b border-dashed border-[color-mix(in_oklab,var(--fg-muted)_45%,transparent)]",
        // transitions
        "transition-[background,border-color] duration-100",
        // hover
        "hover:bg-(--bg-canvas) hover:border-b-[var(--fg-brand)]",
        // focus-visible
        "focus-visible:outline-2 focus-visible:outline-[var(--fg-brand)] focus-visible:outline-offset-[1px] focus-visible:bg-(--bg-canvas) focus-visible:border-b-transparent",
        // group for child icon hover
        "group",
        // alignment
        align === "right" ? "justify-end" : "justify-start",
        className ?? "",
      ].join(" ")}
    >
      {prefix}
      {value}
      {suffix}
      {/* Pencil icon — replaces the former CSS ::after mask */}
      <svg
        aria-hidden
        width="9"
        height="9"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="ml-0.5 shrink-0 opacity-30 transition-opacity duration-100 group-hover:opacity-70"
      >
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
      </svg>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* EditableBoolean                                                              */
/* -------------------------------------------------------------------------- */

export interface EditableBooleanProps {
  /** Current boolean value. */
  value: boolean;
  /** Called when the user toggles the value. */
  onToggle: () => void;
  /** Label when value is true. */
  trueLabel?: string;
  /** Label when value is false. */
  falseLabel?: string;
  /** Accessible label. */
  ariaLabel?: string;
}

/**
 * Toggle-style variant of `EditableValue` for boolean fields.
 * Renders as a small-caps button that toggles between `trueLabel` and
 * `falseLabel`.
 */
export function EditableBoolean({
  value,
  onToggle,
  trueLabel = "Yes",
  falseLabel = "No",
  ariaLabel,
}: EditableBooleanProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={ariaLabel ?? `Toggle ${value ? trueLabel : falseLabel}`}
      aria-pressed={value}
      className={[
        // reset
        "appearance-none bg-transparent p-0 m-0 font-[inherit] text-inherit",
        // layout
        "inline-flex items-center gap-[4px] cursor-pointer",
        "rounded-[4px] py-[1px] px-[5px] -mx-[5px] -my-[1px]",
        // dashed bottom border
        "border-b border-dashed border-[color-mix(in_oklab,var(--fg-muted)_45%,transparent)]",
        "transition-[background,border-color] duration-100",
        "hover:bg-(--bg-canvas) hover:border-b-[var(--fg-brand)]",
        "focus-visible:outline-2 focus-visible:outline-[var(--fg-brand)] focus-visible:outline-offset-[1px]",
        // toggle-specific
        "[font-variant:small-caps] tracking-[0.04em]",
        value ? "text-(--fg-brand)" : "text-(--fg-muted)",
      ].join(" ")}
    >
      {value ? trueLabel : falseLabel}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* EditableSelect                                                              */
/* -------------------------------------------------------------------------- */

/** A single item in an `EditableSelect` dropdown. */
export interface SelectOption {
  /** Text shown in the list. */
  label: string;
  /** Value passed to `onSave` when this item is chosen. */
  value: string;
}

export interface EditableSelectProps {
  /** Current value — matched against `options[].value` to derive the label. */
  value: string;
  /** Called with the chosen option's value when the user picks an item. */
  onSave: (next: string) => void;
  /** List of menu items. */
  options: SelectOption[];
  /** Text alignment of the trigger button. */
  align?: "left" | "right";
  /** Extra class names forwarded to the trigger button. */
  className?: string;
  /** Accessible label for the trigger. */
  ariaLabel?: string;
  /** Optional string rendered before the display label (e.g. "$"). */
  prefix?: string;
  /** Optional string rendered after the display label (e.g. "%"). */
  suffix?: string;
}

/**
 * Dropdown-menu variant of `EditableValue`. Renders as a `<button>` with a
 * chevron affordance; clicking opens an inline options list.
 *
 * Closes on item selection, Escape, or an outside click.
 */
export function EditableSelect({
  value,
  onSave,
  options,
  align = "left",
  className,
  ariaLabel,
  prefix,
  suffix,
}: EditableSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close when the user clicks outside the component
  useEffect(() => {
    if (!open) return;
    function handleMouseDown(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open]);

  function handleTriggerKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  }

  const displayLabel = options.find((o) => o.value === value)?.label ?? value;

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel ?? `Edit ${displayLabel}`}
        className={[
          // reset
          "appearance-none bg-transparent p-0 m-0 font-[inherit] text-inherit",
          // layout
          "inline-flex items-center gap-[4px] cursor-pointer",
          // spacing (mirrors EditableValue)
          "rounded-[4px] py-[1px] px-[5px] -mx-[5px] -my-[1px]",
          // dashed bottom border
          "border-b border-dashed border-[color-mix(in_oklab,var(--fg-muted)_45%,transparent)]",
          // transitions
          "transition-[background,border-color] duration-100",
          // hover
          "hover:bg-(--bg-canvas) hover:border-b-[var(--fg-brand)]",
          // focus-visible
          "focus-visible:outline-2 focus-visible:outline-[var(--fg-brand)] focus-visible:outline-offset-[1px] focus-visible:bg-(--bg-canvas) focus-visible:border-b-transparent",
          // group for chevron icon
          "group",
          // alignment
          align === "right" ? "justify-end" : "justify-start",
          className ?? "",
        ].join(" ")}
      >
        {prefix}
        {displayLabel}
        {suffix}
        {/* Chevron-down icon */}
        <svg
          aria-hidden
          width="9"
          height="9"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="ml-0.5 shrink-0 opacity-30 transition-opacity duration-100 group-hover:opacity-70"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={ariaLabel ?? "Select an option"}
          className={[
            "absolute z-50 top-full mt-1",
            "min-w-[120px] max-h-[240px] overflow-y-auto",
            "bg-(--bg-white) border border-(--border-subtle)",
            "rounded-[6px] shadow-lg py-1",
            align === "right" ? "right-0" : "left-0",
          ].join(" ")}
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="option"
              aria-selected={opt.value === value}
              // Prevent blur-race: focus stays on trigger during mousedown
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (opt.value !== value) onSave(opt.value);
                setOpen(false);
              }}
              className={[
                "w-full text-left px-3 py-1.5 text-sm",
                "transition-colors duration-75",
                "hover:bg-(--bg-canvas)",
                opt.value === value
                  ? "font-medium text-(--fg-brand)"
                  : "text-(--fg-default)",
              ].join(" ")}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* EditableAutocomplete                                                        */
/* -------------------------------------------------------------------------- */

export interface EditableAutocompleteProps {
  /** Current display value. */
  value: string;
  /** Called with the committed string when the user saves. */
  onSave: (next: string) => void;
  /**
   * Async search function called (after `debounceMs`) with the current input
   * text. Return an array of suggestion strings.
   */
  onSearch: (query: string) => Promise<string[]>;
  /** Debounce delay in ms before `onSearch` is invoked. Defaults to 300. */
  debounceMs?: number;
  /** Text alignment of both display and input. */
  align?: "left" | "right";
  /** Extra class names forwarded to both display and input elements. */
  className?: string;
  /** Accessible label. */
  ariaLabel?: string;
  /** Optional string rendered before the value (e.g. "$"). */
  prefix?: string;
  /** Optional string rendered after the value (e.g. "%"). */
  suffix?: string;
  /**
   * When true, any typed text (including values not in the suggestions list)
   * is committed on Enter or blur. Useful for fields that accept free-form
   * input in addition to suggestions (e.g. vendor names).
   */
  allowFreeText?: boolean;
  /**
   * When true, submitting an empty string is treated as a valid commit
   * (calls `onSave("")`). Requires `allowFreeText` to also be set.
   */
  allowEmpty?: boolean;
}

/**
 * Autocomplete variant of `EditableValue`. Switches to a text input on click
 * and calls `onSearch` (debounced) to populate a suggestions dropdown below.
 *
 * Commits on Enter or clicking a suggestion; cancels on Escape.
 * Suggestion items use `onMouseDown` + `preventDefault` so clicking them does
 * not trigger the input's `onBlur` prematurely.
 */
export function EditableAutocomplete({
  value,
  onSave,
  onSearch,
  debounceMs = 300,
  align = "left",
  className,
  ariaLabel,
  prefix,
  suffix,
  allowFreeText = false,
  allowEmpty = false,
}: EditableAutocompleteProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestRef = useRef(0);
  const cancelledRef = useRef(false);
  const committedRef = useRef(false);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  // Clean up any pending debounce timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      searchRequestRef.current += 1;
    };
  }, []);

  function startEdit() {
    cancelledRef.current = false;
    committedRef.current = false;
    // When allowFreeText is set, pre-populate the draft with the current value
    // so users can edit rather than always type from scratch.
    setDraft(allowFreeText ? value : "");
    setSuggestions([]);
    setShowSuggestions(false);
    setEditing(true);
    runSearch("");
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      if (allowFreeText) inputRef.current?.select();
    });
  }

  function runSearch(query: string) {
    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;
    onSearch(query)
      .then((results) => {
        if (requestId !== searchRequestRef.current) return;
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      })
      .catch(() => {
        // silently ignore search errors — field still works as a plain text input
      });
  }

  function commit(next?: string) {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    if (committedRef.current) return;
    committedRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    searchRequestRef.current += 1;
    // If a suggestion was explicitly selected, use it directly
    if (next !== undefined) {
      if (next.trim() !== value.trim()) onSave(next.trim());
      setEditing(false);
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const trimmed = draft.trim();
    if (allowFreeText) {
      // Free-text mode: commit any typed value, including empty when allowEmpty is set
      const isEmptyCommit = trimmed === "" && allowEmpty;
      if (isEmptyCommit || trimmed.length > 0) {
        if (trimmed !== value.trim()) onSave(trimmed);
      }
      // If draft is empty and allowEmpty is false, reset silently
    } else {
      // Default: only save if the typed text matches one of the returned suggestions
      const matched = suggestions.some(
        (s) => s.toLowerCase() === trimmed.toLowerCase(),
      );
      if (matched && trimmed !== value.trim()) {
        onSave(trimmed);
      }
      // If no match, reset silently (don't persist an unrecognised value)
    }
    setEditing(false);
    setSuggestions([]);
    setShowSuggestions(false);
  }

  function cancel() {
    cancelledRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    searchRequestRef.current += 1;
    setDraft(value);
    setEditing(false);
    setSuggestions([]);
    setShowSuggestions(false);
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const q = e.target.value;
    setDraft(q);
    setShowSuggestions(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runSearch(q), debounceMs);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  }

  if (editing) {
    return (
      <div className="relative inline-flex flex-col">
        <input
          ref={inputRef}
          type="text"
          inputMode="text"
          value={draft}
          placeholder={value}
          onChange={handleChange}
          onBlur={() => commit()}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showSuggestions}
          aria-label={ariaLabel}
          className={[
            // reset
            "appearance-none bg-(--bg-white) font-[inherit] text-inherit",
            // sizing
            "min-w-[60px] max-w-full w-auto",
            // spacing
            "py-[1px] px-[4px] -mx-[5px] -my-[2px]",
            // border
            "border border-(--fg-brand) rounded-[4px]",
            // focus ring
            "outline-none focus:shadow-[0_0_0_2px_color-mix(in_oklab,var(--fg-brand)_20%,transparent)]",
            // numeric variant support
            "[font-variant-numeric:inherit]",
            // alignment
            align === "right" ? "text-right" : "text-left",
            className ?? "",
          ].join(" ")}
        />

        {showSuggestions && (
          <div
            role="listbox"
            className={[
              "absolute z-50 top-full mt-1",
              "min-w-[280px] max-h-[240px] overflow-y-auto",
              "bg-(--bg-white) border border-(--border-subtle)",
              "rounded-[6px] shadow-lg py-1",
              align === "right" ? "right-0" : "left-0",
            ].join(" ")}
          >
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                role="option"
                aria-selected={false}
                // Prevent input blur before the click fires
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(s)}
                className="w-full text-left px-3 py-2 text-base text-(--fg-default) hover:bg-(--bg-canvas) transition-colors duration-75"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      aria-label={ariaLabel ?? `Edit ${value}`}
      className={[
        // reset
        "appearance-none bg-transparent p-0 m-0 font-[inherit] text-inherit",
        // layout
        "inline-flex items-center gap-[4px] cursor-text",
        // spacing
        "rounded-[4px] py-[1px] px-[5px] -mx-[5px] -my-[1px]",
        // dashed bottom border
        "border-b border-dashed border-[color-mix(in_oklab,var(--fg-muted)_45%,transparent)]",
        // transitions
        "transition-[background,border-color] duration-100",
        // hover
        "hover:bg-(--bg-canvas) hover:border-b-[var(--fg-brand)]",
        // focus-visible
        "focus-visible:outline-2 focus-visible:outline-[var(--fg-brand)] focus-visible:outline-offset-[1px] focus-visible:bg-(--bg-canvas) focus-visible:border-b-transparent",
        // group for magnifier icon
        "group",
        // alignment
        align === "right" ? "justify-end" : "justify-start",
        className ?? "",
      ].join(" ")}
    >
      {prefix}
      {value}
      {suffix}
      {/* Magnifier icon — signals search capability */}
      <svg
        aria-hidden
        width="9"
        height="9"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="ml-0.5 shrink-0 opacity-30 transition-opacity duration-100 group-hover:opacity-70"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
    </button>
  );
}
